import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { getRangeDates, createDefaultFilters } from './utils/formatters';
import Header from './components/layout/Header';
import FilterBar from './components/filters/FilterBar';
import FileUpload from './components/upload/FileUpload';
import Dashboard from './components/dashboard/Dashboard';
import Planner from './components/planner/Planner';
import CategorizationCenter from './components/categorization/CategorizationCenter';
import AdminPortal from './components/admin/AdminPortal';
import TransactionDrawer from './components/transactions/TransactionDrawer';






const initialSummaryState = {

  totalSpend: 0,

  spendByBank: [],

  spendByCategory: [],

  spendOverTime: [],

  weeklyTrend: [],

  topMerchants: [],

  budgetPerformance: [],

};



function App() {

  const [activeView, setActiveView] = useState('dashboard');

  const [transactions, setTransactions] = useState([]);

  const [summary, setSummary] = useState(initialSummaryState);

  const [loading, setLoading] = useState(true);

  const [editorTransactionId, setEditorTransactionId] = useState(null);

  const [filters, setFilters] = useState(() => createDefaultFilters());



  const buildQueryString = useCallback((activeFilters) => {

    const params = new URLSearchParams();

    if (activeFilters.startDate) params.append('startDate', activeFilters.startDate);

    if (activeFilters.endDate) params.append('endDate', activeFilters.endDate);

    if (activeFilters.category && activeFilters.category !== 'all') params.append('category', activeFilters.category);

    if (activeFilters.bank && activeFilters.bank !== 'all') params.append('bank', activeFilters.bank);

    return params.toString();

  }, []);



  const fetchData = useCallback(async (activeFilters) => {

    setLoading(true);

    try {

      const queryString = buildQueryString(activeFilters);

      const suffix = queryString ? `?${queryString}` : '';

      const [transactionsRes, summaryRes] = await Promise.all([

        axios.get(`/api/transactions${suffix}`),

        axios.get(`/api/summary${suffix}`)

      ]);

      setTransactions(transactionsRes.data);

      setSummary(summaryRes.data);

    } catch (err) {

      console.error("Failed to fetch data:", err);

    } finally {

      setLoading(false);

    }

  }, [buildQueryString]);



  useEffect(() => {

    fetchData(filters);

  }, [filters, fetchData]);



  const handleRangeChange = useCallback((range) => {

    setFilters((prev) => {

      if (range === 'custom') {

        return { ...prev, range };

      }

      const dates = getRangeDates(range);

      return {

        ...prev,

        range,

        startDate: dates.startDate,

        endDate: dates.endDate,

      };

    });

  }, []);



  const handleDateChange = useCallback((field, value) => {

    setFilters((prev) => ({

      ...prev,

      [field]: value,

    }));

  }, []);



  const handleCategoryChange = useCallback((category) => {

    setFilters((prev) => ({

      ...prev,

      category,

    }));

  }, []);



  const handleBankChange = useCallback((bank) => {

    setFilters((prev) => ({

      ...prev,

      bank,

    }));

  }, []);



  const handleResetFilters = useCallback(() => {

    setFilters(createDefaultFilters());

  }, []);



  const categoryOptions = useMemo(() => {

    const set = new Set();

    summary?.spendByCategory?.forEach((item) => set.add(item.name));

    transactions.forEach((txn) => {

      if (txn.category) set.add(txn.category);

    });

    return Array.from(set).filter(Boolean).sort();

  }, [summary, transactions]);



  const bankOptions = useMemo(() => {

    const set = new Set();

    summary?.spendByBank?.forEach((item) => set.add(item.bank));

    transactions.forEach((txn) => {

      if (txn.bank) set.add(txn.bank);

    });

    return Array.from(set).filter(Boolean).sort();

  }, [summary, transactions]);



  const handleUploadSuccess = useCallback(() => {

    fetchData(filters);

  }, [fetchData, filters]);



  const applyCategoryFilter = useCallback((category) => {

    setFilters((prev) => ({

      ...prev,

      category,

    }));

  }, []);



  const applyBankFilter = useCallback((bank) => {

    setFilters((prev) => ({

      ...prev,

      bank,

    }));

  }, []);



  const applyCustomRange = useCallback((startDate, endDate) => {

    setFilters((prev) => ({

      ...prev,

      range: 'custom',

      startDate,

      endDate,

    }));

  }, []);

  const refreshData = useCallback(() => {

    fetchData(filters);

  }, [fetchData, filters]);

  const renderActiveView = () => {
    switch (activeView) {
      case 'dashboard':
        return (
          <>
            <FilterBar
              filters={filters}
              onRangeChange={handleRangeChange}
              onDateChange={handleDateChange}
              onCategoryChange={handleCategoryChange}
              onBankChange={handleBankChange}
              onReset={handleResetFilters}
              categoryOptions={categoryOptions}
              bankOptions={bankOptions}
            />
            <Dashboard
              transactions={transactions}
              summary={summary}
              loading={loading}
              onEditTransaction={setEditorTransactionId}
              onCategoryFilter={applyCategoryFilter}
              onBankFilter={applyBankFilter}
              onRangeFilter={applyCustomRange}
              onDataRefresh={refreshData}
            />
          </>
        );
      case 'upload':
        return <FileUpload onUploadSuccess={handleUploadSuccess} />;
      case 'planner':
        return <Planner summary={summary} />;
      case 'categorize':
        return <CategorizationCenter />;
      case 'admin':
        return <AdminPortal onDataChanged={() => fetchData(filters)} />;
      default:
        return null;
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-night">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-aurora-radial opacity-80" />
        <div className="absolute inset-0 bg-grid-glow opacity-20 [background-size:120px_120px]" />
        <div className="absolute -right-32 top-10 h-72 w-72 rounded-full bg-brand-500/30 blur-3xl" />
        <div className="absolute -left-10 bottom-0 h-72 w-72 rounded-full bg-teal-400/20 blur-[120px]" />
      </div>

      <div className="relative z-10 flex min-h-screen flex-col">
        <Header onNavClick={setActiveView} activeView={activeView} />

        <main className="flex-1 px-4 py-10 md:px-8 lg:px-12">
          <div className="mx-auto flex w-full max-w-7xl flex-col gap-10">
            {renderActiveView()}
          </div>
        </main>

        <TransactionDrawer
          transactionId={editorTransactionId}
          onClose={() => setEditorTransactionId(null)}
          onUpdated={() => fetchData(filters)}
        />
      </div>
    </div>
  );

}



export default App;

